import React from 'react';
import { Box, Typography, Link, alpha, useTheme } from '@mui/material';
import { scaledFontSize } from './createWidgetTheme';
import { getAccessibleColorOnBackground, blendOnBackground } from '../utils/wcagContrast';

const PRIVACY_TINT_ALPHA = 0.05;

interface PrivacyNoticeProps {
  noticeText: string;
  policyUrl?: string | null;
}

/**
 * PrivacyNotice component displays a privacy notice card in the chat UI.
 *
 * Per acceptance criteria:
 * - Shows configurable privacy notice text (falls back to default server-side/client-side)
 * - "Read Policy" link only when policyUrl is provided
 * - Link opens in new tab
 * - Background is lighter version of the institution's primary color
 * - Landmark + h3 heading so VoiceOver/NVDA can revisit the notice without a policy link
 */
export function PrivacyNotice({ noticeText, policyUrl }: PrivacyNoticeProps) {
  const theme = useTheme();
  const headingId = React.useId();
  const noticeBodyId = React.useId();
  const trimmedPolicyUrl = policyUrl?.trim() ?? '';
  const hasPolicyUrl = trimmedPolicyUrl.length > 0;

  const backgroundColor = alpha(theme.palette.primary.main, PRIVACY_TINT_ALPHA);
  const borderColor = alpha(theme.palette.primary.main, 0.1);
  const surfaceBackground = theme.palette.background.default;
  const compositedBackground = blendOnBackground(
    theme.palette.primary.main,
    PRIVACY_TINT_ALPHA,
    surfaceBackground,
  );
  const textColor = getAccessibleColorOnBackground(theme.palette.primary.main, compositedBackground);

  return (
    <Box
      component="section"
      role="region"
      aria-label="Privacy notice"
      aria-labelledby={headingId}
      aria-live="polite"
      aria-atomic="true"
      sx={{
        mx: 2,
        mt: 2,
        mb: 1,
        px: 2,
        py: 1.5,
        backgroundColor,
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
      }}
    >
      <Typography
        component="h3"
        id={headingId}
        sx={{
          fontWeight: 600,
          color: textColor,
          fontSize: scaledFontSize(theme, 14),
          display: 'block',
          mt: 0,
          mx: 0,
          mb: 0.5,
        }}
      >
        Privacy Notice
      </Typography>
      <Typography
        variant="body2"
        component="span"
        sx={{
          color: textColor,
          fontSize: scaledFontSize(theme, 14),
          lineHeight: 1.5,
        }}
      >
        <Box component="span" id={noticeBodyId}>
          {noticeText}
        </Box>
        {hasPolicyUrl && (
          <>
            {' '}
            <Link
              href={trimmedPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Read Policy (opens in a new tab)"
              // Tabbing straight to this link otherwise announced nothing but
              // "Read Policy" — the notice it belongs to was never read out.
              aria-describedby={noticeBodyId}
              sx={{
                color: textColor,
                fontWeight: 500,
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            >
              Read Policy
            </Link>
          </>
        )}
      </Typography>
    </Box>
  );
}
